import { Check } from "lucide-react";
import { motion, type Variants } from "motion/react";

import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const EASE = [0.25, 0.1, 0.25, 1] as const;

const headerVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const stepsVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const stepVariants: Variants = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const viewport = { once: true, margin: "-80px" } as const;

const STEPS = [
  {
    number: "01",
    name: m.landing_step_index_name(),
    description: m.landing_step_index_description(),
    points: [m.landing_step_index_point_1(), m.landing_step_index_point_2(), m.landing_step_index_point_3()],
  },
  {
    number: "02",
    name: m.landing_step_distribute_name(),
    description: m.landing_step_distribute_description(),
    points: [
      m.landing_step_distribute_point_1(),
      m.landing_step_distribute_point_2(),
      m.landing_step_distribute_point_3(),
    ],
    highlighted: true,
  },
  {
    number: "03",
    name: m.landing_step_connect_name(),
    description: m.landing_step_connect_description(),
    points: [m.landing_step_connect_point_1(), m.landing_step_connect_point_2(), m.landing_step_connect_point_3()],
  },
] as const;

/**
 * Three-step walkthrough from a firm's published archive to client workflows.
 * Uses the same card treatment as the rest of the landing: the middle step sits
 * on `bg-primary` tinting so the distribution stage reads as the payoff.
 */
export const HowItWorksSection = () => {
  return (
    <section
      id="how-it-works"
      data-tour="landing-how-it-works"
      className="mx-auto w-full max-w-[1100px] scroll-mt-20 px-5 py-12 md:py-20"
    >
      <motion.div
        className="flex flex-col items-center gap-4 text-center"
        variants={headerVariants}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        <motion.h2 variants={itemVariants} className="text-3xl font-semibold leading-tight text-foreground md:text-5xl">
          {m.landing_how_it_works_title()}
        </motion.h2>
        <motion.p variants={itemVariants} className="max-w-xl text-sm font-medium text-muted-foreground md:text-base">
          {m.landing_how_it_works_subtitle()}
        </motion.p>
      </motion.div>

      <motion.div
        className="mt-10 flex flex-col gap-6 md:flex-row md:items-start"
        variants={stepsVariants}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        {STEPS.map((step) => {
          const highlighted = "highlighted" in step && step.highlighted;
          return (
            <motion.div
              key={step.number}
              variants={stepVariants}
              className={cn(
                "flex flex-1 flex-col gap-6 rounded-xl p-5 md:min-h-[340px]",
                highlighted
                  ? "md:-my-4 md:min-h-[380px] md:py-8 border border-primary/40 bg-primary/10 shadow-lg shadow-primary/5 backdrop-blur-sm"
                  : "border border-border bg-card/40",
              )}
            >
              <div className="flex flex-col gap-3">
                <span
                  className={cn(
                    "text-3xl font-medium leading-none tabular-nums",
                    highlighted ? "text-primary" : "text-muted-foreground",
                  )}
                >
                  {step.number}
                </span>
                <div className="flex flex-col gap-1">
                  <p className="text-base font-medium text-foreground">{step.name}</p>
                  <p className="text-sm font-medium text-muted-foreground">{step.description}</p>
                </div>
              </div>

              <div className="mt-auto flex flex-col gap-3">
                {step.points.map((point) => (
                  <div key={point} className="flex items-start gap-2">
                    <Check className="mt-0.5 size-4 shrink-0 text-primary" strokeWidth={2} />
                    <span className="text-sm leading-tight text-muted-foreground">{point}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
};
